import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';
import googlePlayImage from './assets/images/google-play.png';
import appleStoreImage from './assets/images/apple-store.png';
import civitasLogo from './assets/images/Civitas Signals-small.png';
import consiliumLogo from './assets/images/Consilium-small.png';
import boilWaterAlert from './assets/images/boil-alert-expand.png';
import healthIcon from './assets/images/health-icon-transparent.png';
import crimeIcon from './assets/images/crime-icon-transparent.png';
import environmentalIcon from './assets/images/environmental-icon-transparent.png';
import sentinelImage from './assets/images/sentinel-portrait.png';
import healthAlertsPreview from './assets/images/health-alerts-portrait.png';
import crimeAlertsPreview from './assets/images/crime-alerts-portrait.png';
import environmentalAlertsPreview from './assets/images/envrironmental-alerts-portrait.png';
import exploreScreenPortrait from './assets/images/explore-screen-portrait.png';
import healthIntelligencePortrait from './assets/images/health-intelligence-portrait.png';
import premiumProfilePortrait from './assets/images/premium-profile-portrait.png';
import neighborhoodWatchPortrait from './assets/images/neighborhood-watch-portrait.png';
import VideoEmbed from './components/VideoEmbed';

const GOOGLE_PLAY_URL = process.env.REACT_APP_GOOGLE_PLAY_URL ?? 'https://play.google.com/store/apps/details?id=com.safescope.app&utm_source=na_Med';
const APP_STORE_URL = 'https://apps.apple.com/us/app/consilium-safety/id6760517718';
const PRODUCT_OVERVIEW_VIDEO_EMBED_URL =
  process.env.REACT_APP_PRODUCT_OVERVIEW_VIDEO_URL ??
  'https://www.youtube.com/embed/Mx_iwmftjIA?rel=0&modestbranding=1';
const PRODUCT_OVERVIEW_VIDEO_WATCH_URL =
  process.env.REACT_APP_PRODUCT_OVERVIEW_VIDEO_WATCH_URL ??
  'https://youtu.be/Mx_iwmftjIA';
const PRODUCT_OVERVIEW_VIDEO_POSTER_URL =
  process.env.REACT_APP_PRODUCT_OVERVIEW_VIDEO_POSTER_URL ??
  'https://img.youtube.com/vi/Mx_iwmftjIA/maxresdefault.jpg';
const YOUTUBE_CHANNEL_URL =
  process.env.REACT_APP_YOUTUBE_CHANNEL_URL ?? 'https://youtube.com/@consilium-app';

const getMobileStoreUrl = (userAgent: string): string | null => {
  if (/android/i.test(userAgent)) {
    return GOOGLE_PLAY_URL;
  }

  if (/iphone|ipad|ipod/i.test(userAgent)) {
    return APP_STORE_URL;
  }

  return null;
};

const AppleAppStoreBadge: React.FC<{ imgClassName: string }> = ({ imgClassName }) => (
  <a
    href={APP_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="store-button-wrapper"
    aria-label="Download Consilium on the App Store"
  >
    <img src={appleStoreImage} alt="Download on the App Store" className={imgClassName} />
  </a>
);

const DownloadActions: React.FC<{ badgeClassName?: string }> = ({ badgeClassName = 'store-badge' }) => {
  const storeChoicesRef = useRef<HTMLDivElement>(null);

  const handlePrimaryDownload = () => {
    const storeUrl = typeof navigator === 'undefined' ? null : getMobileStoreUrl(navigator.userAgent);

    if (storeUrl) {
      window.location.assign(storeUrl);
      return;
    }

    storeChoicesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    storeChoicesRef.current?.querySelector<HTMLAnchorElement>('a')?.focus({ preventScroll: true });
  };

  return (
    <div className="download-actions">
      <div className="download-actions-copy">
        <button type="button" className="primary-download-cta" onClick={handlePrimaryDownload}>
          Get Started Free
        </button>
      </div>
      <div className="hero-cta-buttons" ref={storeChoicesRef}>
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get Consilium on Google Play"
        >
          <img src={googlePlayImage} alt="Get it on Google Play" className={badgeClassName} />
        </a>
        <AppleAppStoreBadge imgClassName={badgeClassName} />
      </div>
    </div>
  );
};

const HomePage: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [openAccordionId, setOpenAccordionId] = useState<string | null>('health');
  const [selectedPremiumFeatureId, setSelectedPremiumFeatureId] = useState<string>('monitor-cities');

  const accordionSections = [
    {
      id: 'health',
      title: 'Health Alerts',
      icon: healthIcon,
      iconClass: 'health-icon',
      cardClass: 'health-card',
      summary: "Consilium tracks outbreaks, recalls, and health advisories for your area, and tells you what's confirmed — before it becomes a headline.",
      features: 'Outbreaks – Air Quality – Water Contamination – Food Recalls – 25+ Tracked Diseases',
      previewImage: healthAlertsPreview,
      previewAlt: 'Health alerts preview',
      panelContent: (
        <>
          <p>Consilium tracks outbreaks, recalls, and health advisories for your area, and tells you what's confirmed — before it becomes a headline.</p>
          <p>Stay Protected. Stay Informed.</p>
          <p>A recall hits the shelves. An outbreak starts two zip codes over. A water advisory goes out before the news picks it up. Consilium catches all three — verified, before you ever have to wonder.</p>
          <p>Consilium cuts through the chaos. We monitor thousands of sources—from CDC bulletins to local hospitals—filtering out false alarms to deliver only verified intelligence straight to your phone.</p>
          <h4 className="accordion-subheading">Real-Time Protection</h4>
          <ul className="accordion-list">
            <li>Outbreak alerts before official announcements</li>
            <li>Product recalls affecting your area instantly</li>
            <li>Environmental hazards in your neighborhood</li>
          </ul>
          <h4 className="accordion-subheading">How It Works</h4>
          <ul className="accordion-list">
            <li>We listen everywhere. Health agencies, news feeds, community signals—monitored 24/7.</li>
            <li>We verify everything. AI-powered filtering eliminates false positives.</li>
            <li>We act fast. Critical alerts reach you within minutes.</li>
          </ul>
          <p>Your safety depends on staying ahead of the curve.</p>
          <p>Download Consilium. Protect what matters most.</p>
        </>
      ),
    },
    {
      id: 'crime',
      title: 'Crime Intelligence',
      icon: crimeIcon,
      iconClass: 'crime-icon',
      cardClass: 'crime-card',
      summary: 'Consilium tracks reported crime and emerging patterns in your neighborhood, verified and delivered in plain language.',
      features: 'Shootings – Assaults – Robberies – Carjackings – Neighborhood Trends',
      previewImage: crimeAlertsPreview,
      previewAlt: 'Crime intelligence preview',
      panelContent: (
        <>
          <h4 className="accordion-subheading">Crime Safety Intelligence</h4>
          <p>Consilium tracks reported crime and emerging patterns in your neighborhood, verified and delivered in plain language.</p>
          <p>Know Before Others Know.</p>
          <p>A break-in two blocks over. A carjacking on your commute. A pattern buried three pages into a police blotter. Consilium catches all three — verified, before the next news cycle picks it up.</p>
          <p>Consilium transforms raw safety chatter into verified intelligence. We monitor police scanners, community reports, and official bulletins—filtering the noise to deliver only credible, actionable alerts directly to your phone.</p>
          <h4 className="accordion-subheading">Real-Time Protection</h4>
          <ul className="accordion-list">
            <li>Active incidents unfolding in your neighborhood</li>
            <li>Crime patterns emerging near your routes</li>
            <li>Safety threats before they spread</li>
            <li>Police activity affecting your daily commute</li>
          </ul>
          <h4 className="accordion-subheading">How You Stay Ahead</h4>
          <ul className="accordion-list">
            <li>We monitor everything. Police feeds, community reports, official bulletins—tracked around the clock.</li>
            <li>We verify instantly. Advanced filtering eliminates false alarms and rumors.</li>
            <li>We alert immediately. Critical safety intel hits your device within minutes.</li>
          </ul>
          <p>Your family's safety starts with knowing what's happening now.</p>
          <p>Download Consilium. Stay informed.</p>
        </>
      ),
    },
    {
      id: 'environmental',
      title: 'Environmental Warnings',
      icon: environmentalIcon,
      iconClass: 'environmental-icon',
      cardClass: 'environmental-card',
      summary: "Environmental risk doesn't always come with an official warning in time. Consilium closes that gap.",
      features: 'Wildfires – Chemical Spills – Floods – Toxic Releases – Air Quality',
      previewImage: environmentalAlertsPreview,
      previewAlt: 'Environmental warnings preview',
      panelContent: (
        <>
          <h4 className="accordion-subheading">Know First. Move Calmly.</h4>
          <p>Environmental risk doesn't always come with an official warning in time. Consilium closes that gap.</p>
          <p>A wildfire jumps the ridge. A flash flood closes the highway. A spill shuts down a block. Consilium has it confirmed and on your phone before the news vans show up.</p>
          <p>Consilium monitors thousands of environmental feeds—from weather services to hazmat reports—cutting through bureaucratic delays to deliver verified alerts straight to your phone.</p>
          <h4 className="accordion-subheading">Real-Time Protection</h4>
          <ul className="accordion-list">
            <li>Severe weather approaching your exact location</li>
            <li>Air quality threats affecting your daily routes</li>
            <li>Wildfire evacuations before official orders</li>
            <li>Chemical spills and hazardous material incidents</li>
          </ul>
          <h4 className="accordion-subheading">How It Works</h4>
          <ul className="accordion-list">
            <li>We watch everything. Weather stations, environmental agencies, emergency feeds—monitored 24/7.</li>
            <li>We verify instantly. Advanced filtering separates real threats from false alarms.</li>
            <li>We warn immediately. Critical environmental alerts reach you within minutes.</li>
          </ul>
          <p>Nature is unpredictable. Your safety intelligence doesn't have to be.</p>
          <p>Download Consilium. Stay prepared. Stay informed.</p>
        </>
      ),
    },
  ];

  const premiumFeatures = [
    {
      id: 'monitor-cities',
      label: 'Track Up to 4 Cities at Once',
      title: 'Monitor Four Cities Simultaneously',
      body: (
        <>
          <div className="premium-detail-figure">
            <img src={premiumProfilePortrait} alt="Monitor multiple cities profile" className="premium-detail-image" />
          </div>
          <p>Track up to four regions at once—perfect for multi-location enterprises, security teams, or frequent travelers. Switch between cities instantly or view consolidated dashboards surfacing the most critical alerts across every monitored location.</p>
          <p>No more juggling accounts. Premium delivers your single command center.</p>
        </>
      ),
    },
    {
      id: 'ad-free',
      label: 'Zero Ads. Ever.',
      title: 'Ad-Free Critical Intelligence',
      body: (
        <>
          <p>Premium removes all display and interstitial ads across mobile and web. Critical intelligence should never compete with sponsored content, especially during a crisis.</p>
          <p>Stay focused on the signal, not the noise, when every decision counts.</p>
        </>
      ),
    },
    {
      id: 'roaming-coverage',
      label: 'Coverage That Follows You',
      title: 'Roaming Coverage',
      body: (
        <>
          <div className="premium-detail-figure">
            <img src={premiumProfilePortrait} alt="Consilium roaming coverage" className="premium-detail-image" />
          </div>
          <p>Enable automatic location monitoring so Consilium follows you wherever you go. When roaming activates, the system adjusts alerts to your real-time GPS position, ensuring relevant safety intelligence whether you're commuting across town or traveling cross-country.</p>
          <p>Peace of mind that moves with you.</p>
        </>
      ),
    },
    {
      id: 'base-score-analysis',
      label: 'See Exactly Why Your Score Changed',
      title: 'Deep Base Score Analysis',
      body: (
        <>
          <p>Go beyond top-line health, crime, and environmental scores. Premium unlocks narrative explanations, component-level breakdowns, and recommended mitigation steps.</p>
          <p>Understand exactly why a score moved and how to respond. Accelerate risk assessments with transparent, data-backed intelligence.</p>
        </>
      ),
    },
    {
      id: 'crime-map',
      label: 'Every Incident, Mapped to Your Block',
      title: 'Interactive Crime Mapping',
      body: (
        <>
          <div className="premium-detail-figure">
            <img src={exploreScreenPortrait} alt="Consilium explore crime map" className="premium-detail-image" />
          </div>
          <p>Visualize incidents with an interactive map that filters by severity, category, and recency. See crime patterns emerging in real-time, identify hotspots affecting your routes, and make informed decisions about when and where to travel.</p>
          <p>Your safety strategy starts with seeing the complete picture.</p>
        </>
      ),
    },
    {
      id: 'crime-forecasting',
      label: "Know What's Coming, Not Just What Happened",
      title: 'Consilium Crime Forecasting',
      body: (
        <>
          <div className="premium-detail-figure">
            <img src={sentinelImage} alt="Consilium predictive crime intelligence" className="premium-detail-image" />
          </div>
          <p>Predict where crime will spike before it happens. Our AI analyzes historical patterns, environmental factors, and regional dynamics to forecast crime probability up to 7 days ahead.</p>
          <p>Deploy resources proactively, adjust travel plans, and stay ahead of emerging threats instead of reacting to them.</p>
        </>
      ),
    },
    {
      id: 'crime-patterns',
      label: 'Spot the Pattern Before It Repeats',
      title: 'Crime Pattern Predictions',
      body: (
        <>
          <div className="premium-detail-figure">
            <img src={sentinelImage} alt="Consilium crime pattern predictions" className="premium-detail-image" />
          </div>
          <p>Drill into predictive analytics that highlight pattern shifts across neighborhoods. Anticipate how criminal activity is evolving and prepare safeguards before risk escalates.</p>
        </>
      ),
    },
    {
      id: 'health-forecasting',
      label: 'See an Outbreak Before It Reaches You',
      title: 'Consilium Health Forecasting',
      body: (
        <>
          <div className="premium-detail-figure">
            <img src={healthIntelligencePortrait} alt="Consilium health forecasting visualization" className="premium-detail-image" />
          </div>
          <p>Anticipate disease outbreaks and health emergencies before they peak. Advanced predictive models track early warning signals to forecast health risks in your region.</p>
          <p>Prepare your family and organization with intelligence that lets you act days ahead of official announcements.</p>
        </>
      ),
    },
    {
      id: 'signal-charting',
      label: 'Watch the Trend, Not Just the Alert',
      title: '7-Day Signal Charting',
      body: (
        <>
          <p>Review rolling, multi-domain signal trends with interactive charts that highlight inflection points across health, crime, and environmental domains.</p>
          <p>Understand emerging risk velocity at a glance.</p>
        </>
      ),
    },
    {
      id: 'neighborhood-watch',
      label: 'Community Reports, Verified',
      title: 'Neighborhood Watch Groups',
      body: (
        <>
          <div className="premium-detail-figure">
            <img src={neighborhoodWatchPortrait} alt="Neighborhood watch collaboration" className="premium-detail-image" />
          </div>
          <p>Create and manage private watch groups with neighbors you trust. Share intelligence, coordinate responses, and build community resilience through secure collaboration tools.</p>
          <p>Premium members lead the charge in transforming neighborhoods from reactive communities into proactive safety networks.</p>
        </>
      ),
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      // Show back-to-top button after scrolling 400px
      setShowBackToTop(window.scrollY > 400);

      // Handle fade-in animations
      const sections = document.querySelectorAll('section');
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight * 0.75;
        if (isVisible) {
          section.classList.add('fade-in-section', 'is-visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  return (
    <div className="homepage">
      <nav className="navbar">
        <div className="logo">
          <Link to="/" aria-label="Consilium home">
            <img src={consiliumLogo} alt="Consilium" className="logo-image logo-image-consilium" />
          </Link>
        </div>
        <ul className="nav-links">
          <li><a href="/privacy.html">Privacy Policy</a></li>
          <li><a href="/account-deletion.html">Account Deletion</a></li>
          <li><Link to="/faq">FAQ</Link></li>
          <li><a href="#download" className="nav-cta">Download</a></li>
        </ul>
      </nav>

      <section className="hero">
        <div className="content-with-image">
          <div className="text-content hero-copy">
            <p className="hero-kicker">Consilium Safety</p>
            <h1>Know fast. Act calmly.</h1>
            <p className="hero-benefit">
              Skip the sensational headlines — from a crime trend to a recall to a boil water notice, Consilium tells you the moment it's official, and the moment it's over.
            </p>
            <div className="hero-download" id="download">
              <p className="hero-cta-label">Download Consilium free on Android &amp; iOS</p>
              <DownloadActions />
            </div>
          </div>
          <div className="hero-image">
            <img src={healthAlertsPreview} alt="Consilium health alerts screen" />
          </div>
        </div>
      </section>

      <section
        className="benefit-lines-section fade-in-section"
        aria-label="Consilium benefits"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/sam-3zHvqtpu_neighborhood.jpg)`,
        }}
      >
        <p>Local news needs you scared to keep you watching. Consilium just needs you informed.</p>
        <p>Skip the doomscroll — get the three things that actually changed in your city today.</p>
        <p>You shouldn't have to find the right Facebook group to learn your tap water isn't safe.</p>
      </section>

      <section className="combined-section fade-in-section">
        <div className="combined-header">
          <h2>Consilium Shows You What Matters, So You Can Act Faster</h2>
          <p>We don't just report the news—we analyze it. We break down complex data into easy-to-understand insights so you can make informed decisions. Our AI filters out the noise and shows you the exact signals that matter to you and your family.</p>
        </div>

        <div className="three-column-section">
          <div className="column">
            <div
              className="feature-circle photo-feature-circle"
              style={{
                backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/mason-wilkes-man-on-street.jpg)`,
              }}
            >
              <div className="feature-circle-content">
                <h3>Know First</h3>
              </div>
            </div>
            <p>Get push notifications about verified threats the moment our AI detects them. Always on, always ready, and never in the way.</p>
          </div>
          <div className="column">
            <div
              className="feature-circle photo-feature-circle"
              style={{
                backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/hyper-local.png)`,
              }}
            >
              <div className="feature-circle-content">
                <h3>Hyper Local</h3>
              </div>
            </div>
            <p>That national news story is interesting, but it's not impacting your life. Every Consilium alert is tailored to your exact neighborhood or location.</p>
          </div>
          <div className="column">
            <div
              className="feature-circle photo-feature-circle"
              style={{
                backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/viral-not-verified.png)`,
              }}
            >
              <div className="feature-circle-content">
                <h3>No Noise</h3>
              </div>
            </div>
            <p>Stop hunting through social media or biased sources to get accurate information. Our AI filters through 15,000 daily sources to give you actionable alerts.</p>
          </div>
        </div>
      </section>

      <section className="content-section fade-in-section">
        <div className="content-with-image intro-detail">
          <div className="text-content">
            <h2>Consilium Monitors 15,000+ Sources So You Don’t Have To</h2>
            <p>Consilium uses AI to track crime, health, and environmental signals as they happen, and sends you only what matters — instantly.</p>

            <p>There’s a lot of noise out there. How do you find what matters to you?</p>
            <ul>
              <li>A string of car-jackings in a major city 50 miles from your home. Does that matter to you and your family? Maybe not.</li>
              <li>A sewage leak in a lake near you. Does that matter to you? Maybe.</li>
              <li>An escaped felon last seen in your neighborhood. Does that matter? Definitely.</li>
            </ul>

            <p>We scan official data and breaking news, as well as community reports from all 50 states, and use powerful AI to deliver hyper-local updates the moment you need to know.</p>
          </div>
        </div>
      </section>

      <section id="product-tour" className="product-tour-section fade-in-section">
        <div className="product-tour-inner">
          <div className="product-tour-header">
            <h2>See Consilium in Action</h2>
            <p>
              From your overview dashboard to crime signals and community reports — see how
              Consilium surfaces what matters near you in under three minutes.
            </p>
          </div>
          <VideoEmbed
            embedUrl={PRODUCT_OVERVIEW_VIDEO_EMBED_URL}
            title="Consilium product overview"
            posterSrc={PRODUCT_OVERVIEW_VIDEO_POSTER_URL}
            aspectRatio="16:9"
          />
          <div className="product-tour-footer">
            <a
              href={PRODUCT_OVERVIEW_VIDEO_WATCH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="product-tour-youtube-link"
            >
              Watch on YouTube
            </a>
            <DownloadActions badgeClassName="store-badge store-badge-inline" />
          </div>
        </div>
      </section>

      <section className="content-section fade-in-section">
        <h2>Active Monitoring in 4,000+ Cities</h2>
        <p className="quote">"When something happens near you — a school lockdown, a gas leak down the block — you shouldn't have to dig through social media to find out. Consilium tells you first."</p>
        <p className="quote">- Ron White, Founder</p>

        <div className="boil-alert-section">
          <div className="boil-alert-image">
            <img src={boilWaterAlert} alt="Boil Water Alert" className="boil-alert-img" />
          </div>
          <div className="boil-alert-content">
            <div className="alert-details-box">
              <p>Boil Water Alert - Zion, IL</p>
              <p>Source: Local post (social/news)</p>
              <p>Detected: Immediately upon publication</p>
              <p>Delivered: Instant alert via Consilium</p>
              <p>Estimated public awareness delay: 2–4 hours</p>
              <p>Status: Resolved</p>
            </div>
            <h4>Consilium doesn't wait for virality. It watches quietly, surfaces early, and protects proactively.</h4>
          </div>
        </div>
      </section>

      <section className="content-section comparison-section fade-in-section">
        <h2>Clear Information. Calm Decisions.</h2>

        <table className="comparison-table">
          <caption className="comparison-caption">Why Consilium?</caption>
          <thead>
            <tr className="comparison-header-row">
              <th className="comparison-th comparison-th-feature">Feature</th>
              <th className="comparison-th comparison-th-center">Local News</th>
              <th className="comparison-th comparison-th-center">Social Media</th>
              <th className="comparison-th comparison-th-center consilium-col">Consilium</th>
            </tr>
          </thead>
          <tbody>
            <tr className="comparison-row">
              <td className="comparison-td-feature">Real-time alerts</td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span></td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span><span className="comparison-text">Often delayed</span></td>
              <td className="comparison-td-center consilium-col"><span className="comparison-symbol positive">✅</span></td>
            </tr>
            <tr className="comparison-row">
              <td className="comparison-td-feature">Health, Crime &amp; Environmental coverage</td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span></td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span></td>
              <td className="comparison-td-center consilium-col"><span className="comparison-symbol positive">✅</span></td>
            </tr>
            <tr className="comparison-row">
              <td className="comparison-td-feature">Community-powered reports</td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span></td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span><span className="comparison-text">Noisy, Unverified</span></td>
              <td className="comparison-td-center consilium-col"><span className="comparison-symbol positive">✅</span><span className="comparison-text">Anonymous &amp; Self-policing</span></td>
            </tr>
            <tr className="comparison-row">
              <td className="comparison-td-feature">Predictive threat detection</td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span></td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span></td>
              <td className="comparison-td-center consilium-col"><span className="comparison-symbol positive">✅</span><span className="comparison-text">Health, Crime, Environmental</span></td>
            </tr>
            <tr className="comparison-row">
              <td className="comparison-td-feature">Location-specific signal filtering</td>
              <td className="comparison-td-center"><span className="comparison-symbol positive">✅</span></td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span></td>
              <td className="comparison-td-center consilium-col"><span className="comparison-symbol positive">✅</span><span className="comparison-text">Tailored to your city</span></td>
            </tr>
            <tr className="comparison-row">
              <td className="comparison-td-feature">Trustworthy sources</td>
              <td className="comparison-td-center"><span className="comparison-symbol positive">✅</span></td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span></td>
              <td className="comparison-td-center consilium-col"><span className="comparison-symbol positive">✅</span></td>
            </tr>
            <tr className="comparison-row comparison-row-last">
              <td className="comparison-td-feature">Neighborhood Watch (Premium)</td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span></td>
              <td className="comparison-td-center"><span className="comparison-symbol negative">❌</span></td>
              <td className="comparison-td-center consilium-col"><span className="comparison-symbol positive">✅</span></td>
            </tr>
          </tbody>
        </table>

        <div className="table-cta">
          <p>Ready to stop piecing it together from local news and social feeds?</p>
          <DownloadActions />
        </div>
      </section>

      <section className="content-section-2 fade-in-section">
        <h2>Health. Crime. Environment. All in One App.</h2>
        <div className="everything-section">
          <div className="everything-content accordion">
            {accordionSections.map(section => {
              const isOpen = openAccordionId === section.id;
              const toggleId = `accordion-toggle-${section.id}`;
              return (
                <div
                  key={section.id}
                  className={`category-card accordion-item ${section.cardClass} ${isOpen ? 'open' : ''}`}
                >
                  <button
                    type="button"
                    className="accordion-toggle"
                    aria-expanded={isOpen}
                    aria-controls={`accordion-panel-${section.id}`}
                    id={toggleId}
                    onClick={() => setOpenAccordionId(section.id)}
                  >
                    <span className="category-header">
                      <span className={`category-icon ${section.iconClass}`}>
                        <img src={section.icon} alt={section.title} className="category-icon-img" />
                      </span>
                      <span className="category-heading-text">
                        <span className="category-title">{section.title}</span>
                        {!isOpen && (
                          <span className="accordion-preview" aria-hidden="true">
                            {section.features}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="accordion-indicator" aria-hidden="true">&#8964;</span>
                  </button>
                  <div
                    id={`accordion-panel-${section.id}`}
                    role="region"
                    aria-labelledby={toggleId}
                    className="accordion-panel"
                    hidden={!isOpen}
                  >
                    <div className={`accordion-panel-layout ${section.previewImage ? 'with-image' : ''}`}>
                      <div className="accordion-panel-body">
                        {section.panelContent ?? (
                          <>
                            <p>{section.summary}</p>
                            <p className="feature-list">{section.features}</p>
                          </>
                        )}
                      </div>
                      {section.previewImage && (
                        <div className="accordion-panel-figure">
                          <img src={section.previewImage} alt={section.previewAlt ?? ''} className="accordion-panel-image" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="accordion-cta">
          <p>Ready to know what's happening in your neighborhood?</p>
          <DownloadActions />
        </div>
      </section>
      <section className="content-section fade-in-section">
        <div className="upgrade-section">
          <div className="upgrade-images">
            <img src="/assets/images/johan-mouchet-man-on-phone.jpg" alt="Person checking Consilium on their phone" className="upgrade-image" />
          </div>
          <div className="upgrade-content">
            <h2>Get Started in 60 Seconds</h2>
            <p>It's never been more important to stay informed. Download Consilium and get your first alert within minutes — no credit card required.</p>
            <div className="steps-container">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-text">Download &amp; Set Location — Add Consilium to your device in 60 seconds.</div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-text">Choose Your Alert Preferences — Only receive the alerts that matter to you and your family.</div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-text">One Place to Look — No more piecing it together from social feeds. Just one app, always current.</div>
              </div>
            </div>
            <DownloadActions badgeClassName="store-badge store-badge-inline" />
          </div>
        </div>
      </section>
      <section className="content-section-2 fade-in-section">
        <h2>After You Download: Upgrade to Consilium Premium</h2>
        <p className="premium-intro">Start with Consilium free. When you're ready to see more context, more places, and more predictive intelligence, Premium is the next step inside the app.</p>

        <div className="premium-section">
          <div className="premium-features-layout">
            <div className="premium-features-box">
              <div className="premium-badge">PREMIUM</div>
              <h3>Unlock Premium Features</h3>
              <ul className="premium-feature-list">
                {premiumFeatures.map(feature => {
                  const isSelected = selectedPremiumFeatureId === feature.id;
                  return (
                    <li key={feature.id}>
                      <button
                        type="button"
                        className={`premium-feature-button ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedPremiumFeatureId(feature.id)}
                      >
                        {feature.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="premium-detail-panel">
              <div className="premium-detail-header">
                <h3>{premiumFeatures.find(feature => feature.id === selectedPremiumFeatureId)?.title ?? 'Consilium Premium'}</h3>
              </div>
              <div className="premium-detail-body">
                {premiumFeatures.find(feature => feature.id === selectedPremiumFeatureId)?.body}
                <p className="premium-detail-closing">Premium takes you from knowing what happened to knowing what's coming next.</p>
              </div>
            </div>
          </div>
          <div className="premium-cta">
            <div className="steps-container">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-text">Download App for Free</div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-text">Create your free account</div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-text">Upgrade and try Premium free for 14 days with the code TRYPREMIUM</div>
              </div>
            </div>
            <p className="premium-secondary-note">Download Consilium first, then start your 14-day Premium trial from the app.</p>
          </div>
        </div>
      </section>
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <img src={civitasLogo} alt="Civitas Signals" className="footer-logo" />
            <p>Protecting families with real-time local intelligence.</p>
            <div className="footer-social">
              <a href="https://x.com/civitas_signals" aria-label="Twitter">𝕏</a>
              <a href="https://www.linkedin.com/company/civitas-signals/" aria-label="LinkedIn">in</a>
              <a href="https://www.facebook.com/people/Civitas-Signals/61583581006807/" aria-label="Facebook">f</a>
              <a
                href={YOUTUBE_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="footer-social-youtube"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                  <path
                    fill="currentColor"
                    d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Civitas Signals, Inc. All rights reserved.</p>
          <div className="footer-links">
            <a href="/privacy.html">Privacy Policy</a>
            <a href="/account-deletion.html">Account Deletion</a>
            <Link to="/faq">FAQ</Link>
          </div>
          <DownloadActions badgeClassName="store-badge store-badge-inline" />
        </div>
      </footer>

      {showBackToTop && (
        <button 
          className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
          onClick={scrollToTop}
          aria-label="Back to top"
        />
      )}
    </div>
  );
};

export default HomePage;

